import { useAuth } from '@luminovo/auth';
import { analytics } from '@luminovo/data-stack';
import { CenteredSpinner, isMissingSearchParamError, useRouteMatches } from '@luminovo/design-system';
import * as Sentry from '@sentry/react';
import { type Query, type QueryClient, useQueryErrorResetBoundary } from '@tanstack/react-query';
import React from 'react';

import NotFoundPage from '@/app/Error/NotFoundPage';
import { movedPages } from '@/app/movedPages';
import { RouteAccessDeniedError } from '@/shared/permissions/PagePermissionContext';
import { ErrorFallback } from '@/shared/ui/errors/ErrorBoundary';
import { SadBox } from '@/shared/ui/errors/SadBox';
import { isAppStale } from '@/shared/utils/staleApp';

/** `defaultOnCatch`: reports with React's component stack; Sentry drops `ErrorFallback`'s later report. */
export function reportRouteError(error: unknown, errorInfo: React.ErrorInfo): void {
    // `installStaleAppDetection` logged the lost chunk; `NotFoundPage` logs a missing query param.
    // A route the user may not open isn't a crash: `_authenticated`'s error component shows `NotAuthorizedPage`.
    if (isAppStale() || isMissingSearchParamError(error) || error instanceof RouteAccessDeniedError) return;
    Sentry.captureReactException(error, errorInfo, {
        mechanism: { handled: true, type: 'auto.function.react.error_boundary' },
    });
}

/** `defaultErrorComponent`, for a route component that throws past the page's own boundaries, or a lost route chunk. */
export function RouteErrorComponent({ error }: { error: unknown }): JSX.Element {
    const { reset } = useQueryErrorResetBoundary();
    // `auth0OrgId` only affects token and sign-in requests, and logging out makes none.
    const { isAuthenticated, logout } = useAuth({ auth0OrgId: undefined });
    // Otherwise a suspense query rethrows its cached error once the route's boundary resets.
    // The next query mounting below clears the reset; `removeFailedQueriesOnNavigation` removes unwatched failures.
    React.useEffect(() => {
        reset();
    }, [reset]);
    if (isMissingSearchParamError(error)) return <NotFoundPage reason="missing-search-param" />;
    // A refresh can end on the same error, and where `_authenticated` failed to load, no navbar offers the log-out.
    const logOut = (): void => {
        analytics.track('logout');
        void logout();
    };
    return <ErrorFallback error={error} onLogOut={isAuthenticated ? logOut : undefined} />;
}

/** Renders outside the providers `ErrorFallback` reads, so it falls back to the context-free `SadBox`. */
export function RootErrorComponent(): JSX.Element {
    return <SadBox />;
}

/**
 * `defaultNotFoundComponent`, for a route's `notFound()`: the catch-all `_authenticated/$` throws it for any URL no
 * page claims, so it renders below the navbar. A URL the router can't decode (a stray `%`) matches no route below the
 * root, so it renders in the root's `<Outlet />`, without the signed-in shell.
 * No layout declares its own `notFoundComponent`, the root and `_authenticated` included: the router would render it
 * for every `notFound()` below, in place of the layout (for `_authenticated`, the signed-in shell with the navbar).
 */
function RouteNotFoundComponent(): JSX.Element {
    const pathname = useRouteMatches().at(-1)?.pathname;
    // The router doesn't key it, and `NotFoundPage` decides what to report when it mounts.
    return <NotFoundPage key={pathname} />;
}

/** The router's pending component and each route's suspense fallback; context-free, so public routes can use it. */
export function PlainPendingComponent(): JSX.Element {
    return <CenteredSpinner />;
}

/**
 * The scroll areas a new history entry opens at the top; Back and Forward still restore them. Any other scrolled
 * element carries its position over to the element in the same place on the next page, which suits a layout's menu
 * but not a page. Each selector resets only its first match, so a new kind of page-level scroll area gets an id here,
 * or renders in `PageLayout`.
 */
const scrollToTopSelectors = [
    // A page's own scroll area: `PageLayout`'s, `SidebarLayout`'s content, and the monitoring pages'.
    '#scrollContainer',
    // The PCB tabs' area, inside the PCB layout's `PageLayout`.
    '#pcbScrollContainer',
    // A table that keeps its scroll position (`enablePersistentScrollPosition`).
    '[data-scroll-restoration-id^="tanstack-table-"]',
];

/**
 * Removes, as each navigation starts, failed queries without data that no component watches: a suspense query
 * reading one rethrows the cached error instead of fetching, so a page the user comes back to would open on it,
 * whichever boundary caught it (a route's, a layout's, or one inside the page, such as a dashboard chart's).
 * A watched failed query keeps its error, but `RouteErrorComponent` and `ErrorBoundaryClearedOnNavigation` reset
 * the query error boundary as they clear, so a suspense query below them fetches it again; below a plain boundary
 * inside a page, it stays failed until its watcher refetches it.
 * Removed, not reset: `query.reset()` clears the GC timer, and nothing restarts it while no component watches.
 * `App.tsx` and `renderWithAppRoutes` subscribe it before the first render.
 */
export function removeFailedQueriesOnNavigation(
    router: { subscribe: (event: 'onBeforeLoad', listener: () => void) => () => void },
    queryClient: QueryClient,
): () => void {
    // Sent as a navigation starts, before any route of the next location loads, and not for a preload.
    return router.subscribe('onBeforeLoad', () => {
        queryClient.removeQueries({ predicate: isFailedAndUnwatched });
    });
}

function isFailedAndUnwatched(query: Query): boolean {
    return query.state.status === 'error' && query.state.data === undefined && query.getObserversCount() === 0;
}

/** Options `App.tsx` and `renderWithAppRoutes` both spread in, so the app's route tests run with the same ones. */
export const appRouterOptions = {
    defaultErrorComponent: RouteErrorComponent,
    defaultOnCatch: reportRouteError,
    defaultPendingComponent: PlainPendingComponent,
    defaultNotFoundComponent: RouteNotFoundComponent,
    scrollToTopSelectors,
    // Tab bars and highlights inside a page compare the route on screen with today's routes.
    movedPages,
};
